import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  SUPER_ADMIN_EMAIL,
  SUPER_ADMIN_UID,
  useAuth,
} from "../../auth/AuthProvider";
import PageHeader from "../../components/PageHeader";
import StatTile from "../../components/StatTile";
import Modal from "../../components/Modal";
import { useToast } from "../../components/Feedback";

type Profile = {
  uid: string;
  displayName: string;
  email?: string;
  isAnonymous?: boolean;
  gems: number;
  lives: number;
  wins: number;
  losses: number;
  totalGames: number;
  longestStreak: number;
  currentStreak: number;
  createdAtMs: number;
  banned?: boolean;
  banReason?: string;
  reportCount?: number;
  lastReportedAtMs?: number;
  // Mirror of the `unlimited` custom claim, written by setUnlimited so the
  // panel can show the state without reading auth claims per user.
  unlimited?: boolean;
};

export default function UserDetail() {
  const { uid } = useParams<{ uid: string }>();
  const { user, isSuperAdmin } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [missing, setMissing] = useState(false);
  const [isTargetAdmin, setIsTargetAdmin] = useState(false);
  const [working, setWorking] = useState<
    "ban" | "unban" | "grant" | "revoke" | "unlimited" | null
  >(null);
  const [banOpen, setBanOpen] = useState(false);
  const [banReason, setBanReason] = useState("");
  const { show, node: toast } = useToast();

  // Hedefin yönetici olup olmadığı `admins/{uid}` belgesinden okunuyor —
  // yetkinin tek kaynağı o. Canlı dinliyoruz ki düğmeler kendi yaptığı
  // değişikliği anında yansıtsın.
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      doc(db, "admins", uid),
      (snap) => setIsTargetAdmin(snap.exists()),
      () => setIsTargetAdmin(false)
    );
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          setMissing(true);
          return;
        }
        const x = snap.data();
        setMissing(false);
        setProfile({
          uid: snap.id,
          displayName: (x.displayName as string) ?? "—",
          email: x.email as string | undefined,
          isAnonymous: Boolean(x.isAnonymous),
          gems: Number(x.gems ?? 0),
          lives: Number(x.lives ?? 0),
          wins: Number(x.wins ?? 0),
          losses: Number(x.losses ?? 0),
          totalGames: Number(x.totalGames ?? 0),
          longestStreak: Number(x.longestStreak ?? 0),
          currentStreak: Number(x.currentStreak ?? 0),
          createdAtMs: x.createdAt?.toMillis?.() ?? 0,
          banned: Boolean(x.banned),
          banReason: x.banReason as string | undefined,
          reportCount: Number(x.reportCount ?? 0),
          lastReportedAtMs: x.lastReportedAt?.toMillis?.(),
          unlimited: Boolean(x.unlimited),
        });
      },
      (e) => console.error(e)
    );
    return unsub;
  }, [uid]);

  /**
   * Yasaklama işaretini profile yazar.
   *
   * `setUserBanned` callable'ı yerine doğrudan Firestore: callable'ların
   * Cloud Run servisi kuruluş politikası yüzünden kimliksiz çağrıya kapalı,
   * istek fonksiyona ulaşmadan 403 alıyordu — yani düğme hiç çalışmıyordu.
   * Alanları kurallar koruyor, oyuncu kendi belgesinde `banned` yazamıyor.
   */
  const doBan = async (banned: boolean) => {
    if (!uid) return;
    if (banned && !banReason.trim()) return;
    setWorking(banned ? "ban" : "unban");
    try {
      await updateDoc(doc(db, "users", uid), {
        banned,
        banReason: banned ? banReason.trim() : deleteField(),
        bannedAt: banned ? serverTimestamp() : null,
        bannedBy: banned ? user?.uid ?? null : null,
      });
      setBanOpen(false);
      setBanReason("");
      show(banned ? "Oyuncu yasaklandı." : "Yasak kaldırıldı.");
    } catch (e) {
      show((e as Error).message, "error");
    } finally {
      setWorking(null);
    }
  };

  /**
   * Yönetici yetkisi = `admins/{uid}` belgesinin varlığı.
   *
   * Eskiden grantAdmin/revokeAdmin callable'ları çağrılıyordu; ikisi de bu
   * projede çalışmıyor (IAM 403) ve altlarındaki setCustomUserClaims da
   * yetkisiz. Belge yolunda yetki anında geçerli oluyor.
   */
  const doClaim = async (grant: boolean) => {
    if (!uid) return;
    setWorking(grant ? "grant" : "revoke");
    try {
      if (grant) {
        await setDoc(doc(db, "admins", uid), {
          email: profile?.email ?? null,
          displayName: profile?.displayName ?? null,
          note: null,
          grantedBy: user?.uid ?? null,
          grantedByEmail: user?.email ?? null,
          grantedAt: serverTimestamp(),
        });
        show("Yetki verildi — anında geçerli.");
      } else {
        await deleteDoc(doc(db, "admins", uid));
        show("Yetki kaldırıldı.");
      }
    } catch (e) {
      show((e as Error).message, "error");
    } finally {
      setWorking(null);
    }
  };

  const doUnlimited = async (enabled: boolean) => {
    if (!uid) return;
    setWorking("unlimited");
    try {
      // Doğrudan Firestore'a yazıyoruz. setUnlimited callable'ı bu projede
      // çalışmıyor: Cloud Run servisi kuruluş politikası yüzünden kimliksiz
      // çağrıya kapalı, istek fonksiyona ulaşmadan 403 alıyor. Alanı
      // kurallar koruyor — oyuncu kendi belgesinde `unlimited` alanını
      // değiştiremiyor, sadece yönetici yazabiliyor.
      await updateDoc(doc(db, "users", uid), { unlimited: enabled });
      show(enabled ? "Sınırsız verildi." : "Sınırsız kaldırıldı.");
    } catch (e) {
      show((e as Error).message, "error");
    } finally {
      setWorking(null);
    }
  };

  if (missing) {
    return (
      <div>
        <PageHeader title="Kullanıcı" />
        <div className="card p-10 text-center">
          <div className="text-5xl mb-2">🕳️</div>
          <div className="font-extrabold text-lg mb-1">Bulunamadı</div>
          <div className="text-sm text-textMuted mb-4">
            Böyle bir UID yok. Silinmiş olabilir.
          </div>
          <Link to="/admin/users" className="btn-outline">
            Aramaya dön
          </Link>
        </div>
      </div>
    );
  }
  if (!profile) return <div className="text-textMuted">Yükleniyor…</div>;

  // Proje sahibinin yetkisi güvenlik kurallarına gömülü; panelden alınamaz.
  // Düğmeyi göstermek yanıltıcı olurdu.
  const targetIsOwner =
    profile.uid === SUPER_ADMIN_UID ||
    (profile.email ?? "").toLowerCase() === SUPER_ADMIN_EMAIL;

  return (
    <div className="pb-24">
      <PageHeader
        title={profile.displayName}
        subtitle={profile.uid}
        actions={
          <div className="flex gap-2 flex-wrap">
            {profile.banned ? (
              <button
                className="btn-outline text-sm"
                disabled={working !== null}
                onClick={() => doBan(false)}
              >
                {working === "unban" ? "Kaldırılıyor…" : "🔓 Yasağı kaldır"}
              </button>
            ) : (
              <button
                className="btn-danger text-sm"
                disabled={working !== null}
                onClick={() => setBanOpen(true)}
              >
                🚫 Yasakla
              </button>
            )}
            {isSuperAdmin && !targetIsOwner && (
              <button
                className="btn-outline text-sm"
                disabled={working !== null}
                onClick={() => doClaim(!isTargetAdmin)}
                title="Panele erişim yetkisi"
              >
                {working === "grant" || working === "revoke"
                  ? "…"
                  : isTargetAdmin
                  ? "🔑 Yetkiyi al"
                  : "🔑 Admin yap"}
              </button>
            )}
            {isSuperAdmin && (
              <button
                className="btn-ghost text-sm"
                disabled={working !== null}
                onClick={() => doUnlimited(!profile.unlimited)}
                title="Sınırsız can, sınırsız elmas, reklamsız"
              >
                {profile.unlimited ? "Sınırsızı kaldır" : "Sınırsız yap"}
              </button>
            )}
          </div>
        }
      />

      {(targetIsOwner || isTargetAdmin || profile.unlimited) && (
        <div className="flex gap-2 flex-wrap mb-4">
          {targetIsOwner && (
            <span className="pill bg-gold/20 text-gold border border-gold/40">
              SÜPER YÖNETİCİ
            </span>
          )}
          {isTargetAdmin && !targetIsOwner && (
            <span className="pill bg-primary/20 text-primarySoft border border-primary/40">
              YÖNETİCİ
            </span>
          )}
          {profile.unlimited && (
            <span className="pill bg-diamond/20 text-diamond border border-diamond/40">
              SINIRSIZ
            </span>
          )}
        </div>
      )}

      {profile.banned && (
        <div className="rounded-xl border border-danger/40 bg-danger/10 p-4 mb-6 flex items-start gap-3">
          <div className="text-xl">🚫</div>
          <div>
            <div className="font-extrabold text-danger">YASAKLI</div>
            {profile.banReason && (
              <div className="text-sm text-textSecondary">
                Sebep: {profile.banReason}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="card p-5">
          <div className="section-label mb-3">Hesap</div>
          <Field label="E-posta" value={profile.email ?? "—"} />
          <Field
            label="Tip"
            value={profile.isAnonymous ? "Misafir" : "Hesaplı"}
          />
          <Field
            label="Üyelik"
            value={formatDate(profile.createdAtMs)}
          />
          <Field label="UID" value={profile.uid} mono />
        </div>
        <div className="card p-5">
          <div className="section-label mb-3">Moderasyon</div>
          <Field
            label="Rapor sayısı"
            value={profile.reportCount ?? 0}
            tone={(profile.reportCount ?? 0) > 0 ? "warn" : "neutral"}
          />
          <Field
            label="Son rapor"
            value={
              profile.lastReportedAtMs
                ? formatDate(profile.lastReportedAtMs)
                : "—"
            }
          />
          <Field
            label="Durum"
            value={profile.banned ? "BANLI" : "Aktif"}
            tone={profile.banned ? "bad" : "good"}
          />
          <Field
            label="Panel yetkisi"
            value={
              targetIsOwner
                ? "Süper yönetici"
                : isTargetAdmin
                ? "Yönetici"
                : "Yok"
            }
            tone={targetIsOwner || isTargetAdmin ? "good" : "neutral"}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Galibiyet" value={profile.wins} tone="good" />
        <StatTile label="Mağlubiyet" value={profile.losses} tone="bad" />
        <StatTile label="Toplam maç" value={profile.totalGames} />
        <StatTile
          label="En iyi seri"
          value={profile.longestStreak}
          tone="good"
        />
        <StatTile label="Şimdiki seri" value={profile.currentStreak} />
        <StatTile label="Elmas" value={profile.gems} tone="good" />
        <StatTile label="Can" value={`${profile.lives} / 5`} />
      </div>

      <Modal
        open={banOpen}
        title="Oyuncuyu yasakla"
        subtitle={profile.displayName}
        onClose={() => setBanOpen(false)}
        footer={
          <>
            <button
              className="btn-ghost text-sm"
              onClick={() => setBanOpen(false)}
            >
              Vazgeç
            </button>
            <button
              className="btn-danger text-sm"
              disabled={working !== null || !banReason.trim()}
              onClick={() => doBan(true)}
            >
              {working === "ban" ? "Yasaklanıyor…" : "Yasakla"}
            </button>
          </>
        }
      >
        <label className="section-label block mb-2">
          Sebep (şikayet kayıtlarında görünür)
        </label>
        <input
          className="input"
          placeholder="ör. küfür / taciz"
          value={banReason}
          onChange={(e) => setBanReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && banReason.trim()) void doBan(true);
          }}
        />
      </Modal>

      {toast}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
  tone?: "good" | "bad" | "warn" | "neutral";
}) {
  const cls = {
    good: "text-success",
    bad: "text-danger",
    warn: "text-warning",
    neutral: "text-textPrimary",
  }[tone ?? "neutral"];
  return (
    <div className="flex items-center justify-between py-2 border-b border-stroke/60 last:border-b-0">
      <span className="text-xs text-textMuted font-bold uppercase tracking-wider">
        {label}
      </span>
      <span
        className={[
          "text-sm font-semibold truncate ml-4 max-w-[60%] text-right",
          mono ? "font-mono" : "",
          cls,
        ].join(" ")}
      >
        {value}
      </span>
    </div>
  );
}

function formatDate(ms: number) {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("tr-TR");
}
